import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { TransactionSettingsService } from '@feature/dashboard/settings/transaction-settings/services/transaction-settings.service';
import { PartnerTransactionSettingsInterface } from '@feature/dashboard/settings/transaction-settings/types/transaction-settings';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { CountryInfo } from '@sharedModule/models/country.model';
import { FormService } from '@sharedModule/services/form/form.service';
import { GlobalService } from '@sharedModule/services/global.service';
import { loadDestinationCountry } from '@sharedModule/store/destination-country/destination-country.actions';
import { selectDestinationCountry } from '@sharedModule/store/destination-country/destination-country.selector';
import { selectSourceCountry } from '@sharedModule/store/source-country/source-country.selector';
import { getCountryInfoFromCountryCode } from '@sharedModule/utils/array.utils';
import { Subject, takeUntil } from 'rxjs';
import { CountrySettingsService } from '../../services/country-settings.service';
import { ConversionRateInterface } from '../../types/currency-conversion-rate';
import { UpsertConversionRateInterface } from '../../types/upsert-currency-conversion-rate';

@Component({
  selector: 'app-upsert-conversion-rate',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, InputModule],
  templateUrl: './upsert-conversion-rate.component.html',
  styleUrl: './upsert-conversion-rate.component.scss',
})
export class UpsertConversionRateComponent implements OnInit, OnDestroy {
  public readonly attrConstant =
    ATTRIBUTES_CONSTANT.settings.countrySettings.upsertConversionRate;
  upsertConversationRateForm!: FormGroup;
  sourceCountryList!: CountryInfo[];
  destCountryList!: CountryInfo[];
  transactionSettings?: PartnerTransactionSettingsInterface;
  isEdit!: boolean;
  conversionRateData!: ConversionRateInterface;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private fb: FormBuilder,
    private formService: FormService,
    private globalService: GlobalService,
    private messageHandlerService: MessageHandlerService,
    private countrySettingsService: CountrySettingsService,
    private transactionSettingsService: TransactionSettingsService
  ) {}

  ngOnInit(): void {
    this.upsertConversationRateForm = new FormGroup({
      [this.attrConstant.srcCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.destCountry.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.sourceCurrency.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
      [this.attrConstant.destinationCurrency.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
      [this.attrConstant.transferType.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.serviceLevel.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.paymentType.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.otherFee.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.sellRate.key]: new FormControl('', [
        Validators.required,
        Validators.pattern(/^\d{0,9}(\.\d{0,6})?$/),
      ]),
      [this.attrConstant.markupPercentage.key]: new FormControl(0, [
        Validators.required,
        Validators.pattern(/^\d{0,9}(\.\d{0,2})?$/),
      ]),
      [this.attrConstant.masterRate.key]: new FormControl(
        { value: '', disabled: true },
        [Validators.required]
      ),
      platform_fee: new FormArray([this.getPlatFormArray()]),
    });
    this.store
      .select(selectSourceCountry)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) this.sourceCountryList = data;
      });
    this.isEdit = this._isEdit(this.router.url);
  }

  get f(): Record<string, AbstractControl<string, string>> {
    return this.upsertConversationRateForm.controls;
  }

  getPlatFormArray(): FormGroup {
    return this.fb.group({
      [this.attrConstant.slabAmount.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.fixedFee.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.percentageFee.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.useFixedFee.key]: new FormControl<boolean>(false, [
        Validators.required,
      ]),
    });
  }

  get formArrayData(): FormArray {
    return this.upsertConversationRateForm.controls[
      'platform_fee'
    ] as FormArray;
  }

  addPlatformFee(): void {
    this.formArrayData.push(this.getPlatFormArray());
  }

  removePlatformFee(i: number): void {
    this.formArrayData.removeAt(i);
  }

  private _isEdit = (url: string): boolean => {
    const endUrl = url.split('/');
    if (endUrl[2] == 'edit-conversion-rate') {
      this.globalService
        .getDataStream()
        .pipe(takeUntil(this.#destroy$))
        .subscribe({
          next: data => {
            this.conversionRateData = data;
            if (JSON.stringify(data) === '{}') this.redirectTo();
          },
        });
      this.onSourceCountryChange({
        value: this.sourceCountryList[0].countryCode,
        selectOptions: this.sourceCountryList,
      });
      this.upsertConversationRateForm.patchValue(this.conversionRateData);
      this.upsertConversationRateForm.patchValue({
        src_country: this.conversionRateData.source_country,
        dest_country: this.conversionRateData.destination_country,
      });
      this.onDestCountryChange({
        value: this.conversionRateData.destination_country,
        selectOptions: this.destCountryList,
      });
      this.patchFormArray();
      // The corridor identifies the rate being edited, so it is fixed here: only
      // the rates and fees below it can change. Disabled controls stay in the
      // payload because submit reads getRawValue().
      this.formService.disableControls(this.upsertConversationRateForm, [
        this.attrConstant.srcCountry.key,
        this.attrConstant.destCountry.key,
        this.attrConstant.transferType.key,
        this.attrConstant.serviceLevel.key,
        this.attrConstant.paymentType.key,
      ]);
      return true;
    } else {
      return false;
    }
  };

  patchFormArray(): void {
    const control = this.upsertConversationRateForm.controls[
      'platform_fee'
    ] as FormArray;
    control.removeAt(0);
    for (const key in this.conversionRateData.platform_fee) {
      control.push(
        this.fb.group({
          slab_amount_upto: new FormControl(
            this.conversionRateData.platform_fee[key].slab_amount_upto,
            [Validators.required]
          ),
          fixed_fee: new FormControl(
            this.conversionRateData.platform_fee[key].fixed_fee,
            [Validators.required]
          ),
          percentage_fee: new FormControl(
            this.conversionRateData.platform_fee[key].percentage_fee,
            [Validators.required]
          ),
          use_fixed_fee: new FormControl<boolean>(
            this.conversionRateData.platform_fee[key].use_fixed_fee,
            [Validators.required]
          ),
        })
      );
    }
  }

  onSourceCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    this.store.dispatch(
      loadDestinationCountry({ payload: { src_country: event.value } })
    );
    this.store
      .select(selectDestinationCountry)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) this.destCountryList = data;
      });
    const selectedSourceCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedSourceCountry)
      this.upsertConversationRateForm.patchValue({
        [this.attrConstant.sourceCurrency.key]:
          selectedSourceCountry.currencySupported[0].currency,
      });
  }

  onDestCountryChange(event: {
    value: string;
    selectOptions: CountryInfo[] | null;
  }): void {
    const selectedDestCountry = getCountryInfoFromCountryCode(
      event.value,
      event.selectOptions
    );
    if (selectedDestCountry)
      this.upsertConversationRateForm.patchValue({
        [this.attrConstant.destinationCurrency.key]:
          selectedDestCountry.currencySupported[0].currency,
      });
    this.getPartnerTransactionSettings(event.value);
  }

  getPartnerTransactionSettings(destCountry: string): void {
    this.transactionSettingsService
      .getPartnerTransactionSettings({
        // getRawValue, not value: src_country is disabled while editing and
        // would otherwise drop out, leaving the option lists empty.
        src_country: this.upsertConversationRateForm.getRawValue().src_country,
        dest_country: destCountry,
      })
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) this.transactionSettings = x.response;
        },
      });
  }

  upsertConversationRateSubmit(): void {
    if (this.upsertConversationRateForm.valid) {
      const formValue = this.upsertConversationRateForm.getRawValue();
      const payload: UpsertConversionRateInterface = {
        transfer_type_po_name: this.transactionSettings!.transfer_types.find(
          x => x.value === formValue[this.attrConstant.transferType.key]
        )?.name,
        payment_type_name: this.transactionSettings!.payment_method_codes.find(
          x => x.imgPath === formValue[this.attrConstant.paymentType.key]
        )?.name,
        service_level_name:
          this.transactionSettings!.service_level_delivery_speed_codes.find(
            x => x.code === formValue[this.attrConstant.serviceLevel.key]
          )?.name,
        ...formValue,
      };
      if (this.isEdit)
        this.updateConversationRate({
          id: this.conversionRateData.id,
          ...payload,
        });
      else this.addConversationRate(payload);
    }
  }

  addConversationRate(formValue: UpsertConversionRateInterface): void {
    this.countrySettingsService
      .addConversionRate(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.redirectTo();
          }
        },
      });
  }

  updateConversationRate(formValue: UpsertConversionRateInterface): void {
    this.countrySettingsService
      .updateConversionRate(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.messageHandlerService.show(x.message);
            this.redirectTo();
          }
        },
      });
  }

  onKeyUp(): void {
    const calcData = this.upsertConversationRateForm.value;
    if (
      calcData.sell_rate !== '' &&
      calcData.sell_rate !== null &&
      calcData.markup_percentage !== null
    ) {
      const calcMasterRate =
        calcData.markup_percentage === 0
          ? calcData.sell_rate
          : (calcData.sell_rate / 100) * calcData.markup_percentage +
            calcData.sell_rate;
      const lengthAfterDecimal =
        calcMasterRate.toString().split('.').length === 2
          ? calcMasterRate.toString().split('.')[1].length > 6
            ? Number(calcMasterRate.toFixed(6))
            : calcMasterRate
          : calcMasterRate;
      this.upsertConversationRateForm.patchValue({
        master_rate:
          !this.f['sell_rate'].hasError('required') &&
          !this.f['sell_rate'].hasError('pattern') &&
          !this.f['markup_percentage'].hasError('pattern') &&
          !this.f['markup_percentage'].hasError('required')
            ? lengthAfterDecimal
            : '',
      });
    }
  }

  redirectTo(): void {
    this.globalService.currentTabIndex = 2;
    this.router.navigate(['/settings/country']);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
